import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowUp } from 'react-icons/fi';
import { useLocation } from 'react-router-dom';

// Singleton guard - prevents double mounting even in React Strict Mode
const SINGLETON_KEY = '__faithinsight_scroll_to_top_singleton__';

// Scroll thresholds with hysteresis to prevent flickering
const SHOW_THRESHOLD = 300; // Show button when scrolled past this
const HIDE_THRESHOLD = 200; // Hide button when scrolled back above this

/**
 * Global Scroll-to-Top FAB
 * - TRUE SINGLETON: Only one instance ever renders (window-level guard)
 * - Shows on mobile when scrolled > 300px
 * - Positioned above bottom nav on mobile
 * - Course Detail page: positioned above floating bottom nav
 * - Uses hysteresis to prevent flickering near threshold
 */
function GlobalScrollToTop() {
  // All hooks MUST be called before any conditional returns (Rules of Hooks)
  const { pathname } = useLocation();
  const [showButton, setShowButton] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const lastScrollTop = useRef(0);
  
  // Check if on course detail page (memoized)
  const isCourseDetailPage = useMemo(() => {
    return pathname.includes('/course/');
  }, [pathname]);

  const isQuizAttemptPage = useMemo(() => {
    return pathname.includes('/student/attempt/');
  }, [pathname]);

  const handleScroll = useCallback(() => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    // Hide on pages with their own dense/fixed navigation controls.
    if (isCourseDetailPage || isQuizAttemptPage) {
      setShowButton(false);
      return;
    }
    
    // Use hysteresis to prevent flickering:
    // - Show when scrolling down past SHOW_THRESHOLD
    // - Hide when scrolling up past HIDE_THRESHOLD
    setShowButton(prev => {
      if (prev) {
        // Currently showing - only hide if scrolled above HIDE_THRESHOLD
        return scrollTop > HIDE_THRESHOLD;
      } else {
        // Currently hidden - only show if scrolled past SHOW_THRESHOLD
        return scrollTop > SHOW_THRESHOLD;
      }
    });
    
    lastScrollTop.current = scrollTop;
  }, [isCourseDetailPage, isQuizAttemptPage]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  useEffect(() => {
    // Singleton guard - if already claimed, don't activate this instance
    if (typeof window !== 'undefined') {
      if (window[SINGLETON_KEY]) {
        console.warn('GlobalScrollToTop: Another instance already mounted, this instance will remain inactive');
        setIsActive(false);
        return;
      }
      window[SINGLETON_KEY] = true;
      setIsActive(true);
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      // Release singleton on unmount
      if (typeof window !== 'undefined') {
        window[SINGLETON_KEY] = false;
      }
    };
  }, [handleScroll]);

  // Don't render anything if this instance is not active or route-specific controls would conflict.
  if (!isActive || isCourseDetailPage || isQuizAttemptPage) {
    return null;
  }

  // env(safe-area-inset-bottom) only covers the fixed home-indicator gesture bar
  // (~34px) - it does NOT cover Safari's separate, transient URL/tab toolbar, which
  // isn't exposed via any CSS env() variable or reliably detectable via
  // window.innerHeight / visualViewport (tried - iOS Safari doesn't consistently
  // diverge the two the way that trick relies on). Chrome on iOS doesn't have this
  // toolbar-overlap behavior at all, which is why this only ever showed up in Safari.
  // Falling back to a static buffer generous enough to clear Safari's toolbar even
  // in its fully-expanded state.
  const bottom = 'calc(env(safe-area-inset-bottom) + 90px)';

  return (
    <AnimatePresence>
      {showButton && (
        <motion.button
          type="button"
          aria-label="Scroll to top"
          onClick={scrollToTop}
          initial={{ opacity: 0, scale: 0.9, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 8 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          style={{
            position: 'fixed',
            right: '16px',
            bottom,
            zIndex: 45,
            width: '52px',
            height: '52px',
            borderRadius: '9999px',
            border: 'none',
            cursor: 'pointer',
            background: 'var(--primary-color, #B08B57)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
          }}
        >
          <FiArrowUp size={20} color="#fff" />
        </motion.button>
      )}
    </AnimatePresence>
  );
}

export default GlobalScrollToTop;
